/**
 * Analytics provider: Google Tag Manager + PostHog (same event schema via gtmService).
 */

import React, { useEffect } from 'react';
import { useLocation } from 'react-router';
import { gtmService } from '../services/gtmService';
import { getEnvironmentConfig } from '../config/environment';
import { posthogService, resolvePostHogKey } from '../services/posthogService';
import { isMobileViewport } from '../util/isMobileViewport';
import { scheduleOnInteraction } from '../util/scheduleOnInteraction';

interface GTMProviderProps {
  children: React.ReactNode;
}

export const GTMProvider: React.FC<GTMProviderProps> = ({ children }) => {
  const location = useLocation();

  // Initialize GTM + PostHog after interaction or timeout so they do not compete with LCP.
  useEffect(() => {
    const analyticsTimeoutMs = isMobileViewport() ? 12000 : 5000;
    return scheduleOnInteraction(() => {
      const config = getEnvironmentConfig();
      let gtmId: string | null = null;

      // Get GTM ID based on environment
      if (config.name === 'production') {
        gtmId = import.meta.env.VITE_GTM_ID || null;
      } else if (config.name === 'staging') {
        gtmId = import.meta.env.VITE_GTM_ID_STAGING || import.meta.env.VITE_GTM_ID || null;
      } else {
        // Local development - use staging or production ID if provided, or skip
        gtmId = import.meta.env.VITE_GTM_ID_LOCAL ||
                 import.meta.env.VITE_GTM_ID_STAGING ||
                 import.meta.env.VITE_GTM_ID ||
                 null;
      }

      const posthogKey = resolvePostHogKey();
      if (posthogKey) {
        posthogService.init(posthogKey);
      } else if (process.env.NODE_ENV === 'development') {
        console.log('[PostHog] No project key configured for', config.name, 'environment');
      }

      if (gtmId) {
        // Initialize dataLayer (must be before GTM script)
        if (typeof window !== 'undefined' && !window.dataLayer) {
          window.dataLayer = [];
        }

        // Inject GTM script using the official GTM snippet format
        // This matches the exact format provided by Google Tag Manager
        const gtmScript = document.createElement('script');
        gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;
        document.head.insertBefore(gtmScript, document.head.firstChild);

        // Inject GTM noscript into body (immediately after opening body tag)
        const noscript = document.createElement('noscript');
        noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
        document.body.insertBefore(noscript, document.body.firstChild);

        // Initialize GTM service
        gtmService.initialize(gtmId);
      } else if (process.env.NODE_ENV === 'development') {
        console.log('[GTM] No GTM container ID configured for', config.name, 'environment');
      }
    }, analyticsTimeoutMs);
  }, []);

  // Track page views on route changes (GTM + PostHog)
  useEffect(() => {
    if (!gtmService.isInitialized() && !posthogService.isReady()) {
      return;
    }
    const timer = setTimeout(() => {
      gtmService.trackPageView(document.title, location.pathname + location.search);
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  return <>{children}</>;
};
